import { CardDetails } from './components/CardDetails';
import { InvoiceDetails } from './components/InvoiceDetails';

import { ButtonElement } from '../shared/buttonElement/ButtonElement';
import { LoaderElement } from '../shared/loaderElement/LoaderElement';
import { SectionElement } from '../shared/sectionElement/SectionElement';
import type { BillingElementProps } from './BillingElement.types';

const CADENCE_TO_INTERVAL: Record<string, string> = {
  monthly: 'month',
  annual: 'year',
  quarterly: 'quarter',
};

const getIntervalLabel = (cadence: string, translations: BillingElementProps['translations']) => {
  if (!cadence) return '';
  switch (cadence) {
    case 'month':
      return translations?.['billing.summary.intervals.month'];
    case 'quarter':
      return translations?.['billing.summary.intervals.quarter'];
    case 'year':
      return translations?.['billing.summary.intervals.year'];
    case 'one_time':
      return translations?.['billing.summary.intervals.one_time'];
    default:
      return cadence;
  }
};

export function BillingElement(props: BillingElementProps) {
  const { translations, children, element, isLoading, onClickUpdate, isUpdateButtonDisabled } = props;
  const { data } = element.attrs || {};

  if (isLoading) {
    return (
      <SectionElement title={translations?.['billing.summary.title']}>
        <LoaderElement count={4} />
      </SectionElement>
    );
  }

  return (
    <SectionElement title={translations?.['billing.summary.title']}>
      {data?.tierInvoices?.map((invoices, index) => {
        const paidTrial = invoices.paid_trial_info;

        return (
          <div className="flex flex-col gap-2" key={`${invoices.tier_name}-${index}`}>
            {data.tierInvoices.length > 1 && (
              <h5 className="text-base leading-6 font-medium text-gray-900 capitalize">{invoices.tier_name}</h5>
            )}
            <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
              <InvoiceDetails
                title={
                  paidTrial
                    ? translations?.['billing.summary.paid_trial.current_heading']
                    : translations?.['billing.summary.last_payment_heading']
                }
                timestampPrefix={
                  paidTrial
                    ? translations?.['billing.summary.paid_trial.current_timestamp_prefix']
                    : translations?.['billing.summary.last_timestamp_prefix']
                }
                timestamp={paidTrial ? paidTrial.started_at : invoices.last_invoice?.timestamp}
                amountCents={paidTrial ? paidTrial.trial_price_cents : invoices.last_invoice?.amount_cents}
                originalAmountCents={paidTrial ? undefined : invoices.last_invoice?.original_amount_cents}
                currency={paidTrial ? paidTrial.currency : invoices.last_invoice?.currency}
                interval={
                  paidTrial
                    ? translations?.['billing.summary.paid_trial.trial_duration_label']
                    : invoices.last_invoice?.cadence
                      ? getIntervalLabel(invoices.last_invoice.cadence, translations)
                      : undefined
                }
                locale={data?.locale}
                placeholderText={paidTrial ? undefined : translations?.['billing.summary.n_a']}
                discount={paidTrial ? undefined : invoices.last_invoice?.discount}
              />
              <InvoiceDetails
                title={translations?.['billing.summary.next_heading']}
                timestampPrefix={translations?.['billing.summary.next_timestamp_prefix']}
                timestamp={paidTrial ? paidTrial.ends_at : invoices.next_invoice?.timestamp}
                amountCents={paidTrial ? paidTrial.regular_price_cents : invoices.next_invoice?.amount_cents}
                originalAmountCents={paidTrial ? undefined : invoices.next_invoice?.original_amount_cents}
                currency={paidTrial ? paidTrial.currency : invoices.next_invoice?.currency}
                interval={
                  paidTrial
                    ? paidTrial.regular_billing_cadence
                      ? getIntervalLabel(
                          CADENCE_TO_INTERVAL[paidTrial.regular_billing_cadence] ?? paidTrial.regular_billing_cadence,
                          translations,
                        )
                      : undefined
                    : invoices.next_invoice?.cadence
                      ? getIntervalLabel(invoices.next_invoice.cadence, translations)
                      : undefined
                }
                locale={data?.locale}
                placeholderText={translations?.['billing.summary.n_a']}
                discount={paidTrial ? undefined : invoices.next_invoice?.discount}
              />
            </div>
          </div>
        );
      })}

      {data?.billing_details?.brand && data?.billing_details?.last4 && (
        <CardDetails
          brand={data.billing_details.brand}
          last4={data.billing_details.last4}
          isLoading={isUpdateButtonDisabled}
          onClickUpdate={onClickUpdate}
          translations={translations}
        />
      )}
      <div className="w-full flex items-center justify-center">
        <ButtonElement onClick={onClickUpdate} variant="flush" disabled={isUpdateButtonDisabled}>
          {translations?.['billing.details.show_address_button_label']}
        </ButtonElement>
      </div>

      {children}
    </SectionElement>
  );
}
